import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { newIdempotencyKey } from '../client/idempotency';
import { queryKeys } from '../client/query-client';
import { Comment, PollOption, PollView, Post, PostAttachment, ReactionKind } from '../cases/cases';
import { CohortAudience } from '../content/contracts';
/**
 * Community (spec §9).
 *
 * `moderationMode` round-trips on a room and nothing reads it. Reporting and
 * blocking (App Store guideline 1.2, 24 Sep 2026) live beside this file in
 * `safety.ts`: the report goes to the moderators' queue, the block changes
 * only what the caller sees.
 */
export { Post, Comment, PollOption, PollView, PostAttachment, ReactionKind };
export const HouseRulesSection = z.object({
    id: z.string().uuid(),
    kind: z.enum(['RULE', 'PROMISE']),
    position: z.number().nonnegative(),
    title: z.string(),
    body: z.string().optional(),
});
export const HouseRules = z.object({
    id: z.string().uuid(),
    /** The published version people accept — "v3, in force since 12 May 2026". */
    revision: z.number().int().positive(),
    status: z.enum(['DRAFT', 'IN_FORCE', 'SUPERSEDED']),
    inForceSince: z.string().datetime().nullable(),
    sections: z.array(HouseRulesSection),
});
/**
 * Whether the caller must accept before entering.
 *
 * `reason` distinguishes four cases and the UI uses three of them: never
 * accepted, superseded (the rules changed since they agreed), and no rules in
 * force — which is NOT a gate, because blocking a community behind rules
 * nobody has written would be our bug presented as their obligation.
 */
export const HouseRulesGate = z.object({
    blocked: z.boolean(),
    reason: z.enum(['NO_RULES_IN_FORCE', 'ALREADY_ACCEPTED', 'NEVER_ACCEPTED', 'SUPERSEDED']),
    revision: z.number().int().positive().nullable(),
    rules: HouseRules.nullable(),
});
export function useHouseRulesGate(http, locale) {
    return useQuery({
        queryKey: queryKeys.community.houseRulesGate,
        queryFn: () => http.get('/community/house-rules/gate', { schema: HouseRulesGate, locale }),
    });
}
export function useAcceptHouseRules(http) {
    const queryClient = useQueryClient();
    return useMutation({
        /*
         * The accept answers with the FRESH gate — `blocked: false, reason:
         * ALREADY_ACCEPTED` — precisely so the client can move on without a second
         * request. This used to parse it as `{ ok: boolean }`, which the response
         * has never carried: the parse threw, `onSuccess` never ran, and the
         * member stayed on the gate until a manual refresh re-read it.
         */
        mutationFn: (revision) => http.post('/community/house-rules/accept', { revision }, {
            schema: HouseRulesGate,
            idempotencyKey: newIdempotencyKey(),
        }),
        // Seeded straight into the cache rather than invalidated: the response IS
        // the gate's new state, so the community page unblocks on the click.
        onSuccess: (gate) => {
            queryClient.setQueryData(queryKeys.community.houseRulesGate, gate);
            void queryClient.invalidateQueries({ queryKey: queryKeys.community.houseRulesGate });
        },
    });
}
/**
 * The rules in force, readable outside the gate.
 *
 * The gate's `rules` is null the moment the caller has accepted — it answers
 * "must I accept", not "what did I accept". The landing's House Rules card has
 * to keep rendering after acceptance, so it reads this instead. `null` when no
 * revision is in force, which is a real state and not an error.
 */
export function useCurrentHouseRules(http, locale) {
    return useQuery({
        queryKey: ['community', 'house-rules', 'current', locale],
        queryFn: () => http.get('/community/house-rules/current', {
            schema: z.object({ rules: HouseRules.nullable() }),
            locale,
        }),
    });
}
export const CommunityPage = z.object({
    audience: z.string(),
    /** The room spotlighted at the top, or null when none is chosen. */
    featuredRoomId: z.string().uuid().nullable(),
    howItWorks: z.string().optional(),
});
export const RoomCategory = z.object({
    id: z.string().uuid(),
    key: z.string(),
    name: z.string(),
    position: z.number().nonnegative(),
});
export const Room = z.object({
    id: z.string().uuid(),
    categoryId: z.string().uuid(),
    key: z.string(),
    name: z.string(),
    description: z.string().optional(),
    position: z.number().nonnegative(),
    featured: z.boolean(),
    premium: z.boolean(),
    /**
     * Who can SEE and who can POST are two separate settings, deliberately.
     * An empty `posting` list is a READ-ONLY room, never an open one.
     *
     * `audiences` was missing from this schema until 2026-08-05 while the
     * comment below already referred to it — so the server sent it, Zod stripped
     * it as an unknown key, and nothing failed. That is the quiet failure mode of
     * a hand-written mirror: a field you never declared is a field you can never
     * use, and there is no error to tell you.
     */
    audiences: z.array(z.string()),
    posting: z.array(z.string()),
    /**
     * The admin-picked icon — the room card's illustration derives from it.
     * Declared for the same reason `audiences` is: the server has always sent
     * it, and an undeclared key is one Zod strips silently.
     */
    icon: z.enum(['USERS', 'MOON', 'WIND', 'SHIELD', 'QUOTE', 'FLAG', 'PHONE']),
    /**
     * The shared age bands, several at once (TSP request 3, 25 Sep 2026). Empty
     * is a room for every age. Defaulted so a server predating it still parses.
     */
    ageBandIds: z.array(z.string().uuid()).default([]),
    /** **Deprecated** — the first of `ageBandIds`, or null. Read `ageBandIds`. */
    ageBandId: z.string().uuid().nullable().optional(),
    /**
     * The cohorts and pods that see this room, beside `audiences` (TSP request
     * 2). Empty is « no cohort ». A cohort-only room has an EMPTY `audiences` —
     * which is why the landing groups on this first and never reads an empty
     * `audiences` as « for parents ».
     */
    cohorts: z.array(CohortAudience).default([]),
    /**
     * The room's picture (16 Sep 2026), authored in the back office. `coverUrl`
     * is a short-lived signed address — render it, never store it. Null draws
     * the room's monogram: the stock-photo fallback is gone.
     */
    coverAssetId: z.string().uuid().nullable(),
    coverUrl: z.string().nullable(),
    /** Resolved for the caller — we never derive it from the two lists above. */
    canPost: z.boolean(),
    /**
     * Whether the caller moderates this room — pins, hides, edits and deletes its
     * posts (TSP request 9). Staff everywhere; a TSP facilitator on their own
     * cohort's rooms. Absent from a server that predates the field — read as no.
     */
    canModerate: z.boolean().optional(),
    postCount: z.number().int().nonnegative(),
    memberCount: z.number().int().nonnegative(),
    /**
     * A named window, not a rate — the server counts the trailing seven days.
     * The About card's "posts a week" figure; the all-time `postCount` stays
     * beside it because they answer different questions.
     */
    postsLastWeek: z.number().int().nonnegative(),
    /**
     * Membership is a FOLLOW and grants nothing — confirmed in round 6.
     *
     * It changes what your surfaces show and which posts fall inside the bookmark
     * scope. `audiences` still decides who sees, `posting` still decides who
     * acts, and `canPost` is unmoved by joining. A read-only room still refuses a
     * member who has joined it.
     */
    joined: z.boolean(),
    /**
     * Everyone the room is for is a member and cannot leave (8 Oct 2026 — the
     * parents' and the practitioners' « salle communautaire »), and the team is in
     * every such room. No Leave control is drawn on it.
     */
    autoJoin: z.boolean().default(false),
});
/**
 * The one audience a room is labelled with, or null when it is genuinely mixed.
 * A room for practitioners and TSP members is a practitioners' room — TSP members
 * are practitioners in a cohort (8 Oct 2026: the practitioners' « salle
 * communautaire » took them in, and its chip turned into « Mixte »).
 */
export function roomAudienceLabel(audiences) {
    const own = audiences.length > 1 && audiences.includes('PRO') ? audiences.filter((a) => a !== 'TSP') : audiences;
    return own.length === 1 ? own[0] : null;
}
/**
 * The room Home's community block shows (8 Oct 2026): a Parent's auto-joined
 * parents' room, a Pro's, organisation admin's or TSP member's practitioners' room. Null —
 * the ordinary feed of every followed room — for anybody else, or before the
 * room list has loaded.
 */
export function homeRoomFor(rooms, platformRole) {
    // TSP members are in the practitioners' room too (operator, 8 Oct 2026).
    const audience = platformRole === 'PARENT' ? 'PARENT' : ['PRO', 'ORG_ADMIN', 'TSP'].includes(platformRole) ? 'PRO' : null;
    if (!audience)
        return null;
    return rooms.find((room) => room.autoJoin && room.joined && room.audiences.includes(audience)) ?? null;
}
export function useCommunityPage(http, locale) {
    return useQuery({
        queryKey: ['community', 'page', locale],
        queryFn: () => http.get('/community/page', { schema: CommunityPage, locale }),
    });
}
export function useRoomCategories(http, locale) {
    return useQuery({
        queryKey: queryKeys.community.categories,
        queryFn: () => http.get('/community/categories', {
            schema: z.object({ items: z.array(RoomCategory), locale: z.string() }),
            locale,
        }),
    });
}
const RoomPage = z.object({
    items: z.array(Room),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    locale: z.string(),
});
/** The server's cap on `pageSize` for this list (`CommunityListQuery`). */
export const ROOMS_PAGE_SIZE = 200;
/**
 * A backstop, not a limit anyone should meet: 50 pages of 200 is 10 000
 * rooms. It exists so a server that never stops answering with a full page
 * cannot spin this loop forever.
 */
const ROOMS_MAX_PAGES = 50;
/**
 * Every room the caller can see, across every page.
 *
 * `GET /community/rooms` is paged (default 50, max 200) and this used to read
 * page one only — so the 51st room was missing from the landing, the side
 * nav, and every screen that finds "its" room by id in this list (a room
 * screen past the first fifty rendered with no header). It now asks at the
 * cap and walks `page` until it holds `total` rooms, or a page comes back
 * short. `total` counts what the caller may see, so it is the right stop.
 */
export async function fetchAllRooms(http, locale) {
    const items = [];
    let total = 0;
    let resolved = locale;
    for (let page = 1; page <= ROOMS_MAX_PAGES; page += 1) {
        const answer = await http.get('/community/rooms', {
            query: { page, pageSize: ROOMS_PAGE_SIZE },
            schema: RoomPage,
            locale,
        });
        items.push(...answer.items);
        total = answer.total;
        resolved = answer.locale;
        if (answer.items.length < ROOMS_PAGE_SIZE || items.length >= total)
            break;
    }
    // A room that moved between two page reads can arrive twice; keep the first.
    const seen = new Set();
    const unique = items.filter((room) => (seen.has(room.id) ? false : (seen.add(room.id), true)));
    return { items: unique, total, locale: resolved };
}
export function useRooms(http, locale) {
    return useQuery({
        queryKey: queryKeys.community.rooms,
        queryFn: () => fetchAllRooms(http, locale),
    });
}
const pagedPlain = (item) => z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
/**
 * A content item's thread — every type, not only case studies.
 *
 * The rule that permits it was settled when §0 was fixed: *anyone who may read
 * a content item may discuss it.* Refuses on `CONTENT_NOT_FOUND` — a code that
 * names no type, because a route whose point is that the caller need not know
 * the type must not leak it in a refusal.
 */
export function useContentPosts(http, contentItemId) {
    return useQuery({
        queryKey: ['community', 'content', contentItemId, 'posts'],
        enabled: Boolean(contentItemId),
        queryFn: () => http.get(`/community/content/${contentItemId}/posts`, { schema: pagedPlain(Post) }),
    });
}
export function useCreateContentPost(http, contentItemId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ body, attachmentIds }) => http.post(`/community/content/${contentItemId}/posts`, {
            body,
            ...(attachmentIds?.length ? { attachmentIds } : {}),
        }, {
            schema: Post,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['community', 'content', contentItemId, 'posts'] }),
    });
}
export function useRoomPosts(http, roomId, page = 1) {
    return useQuery({
        queryKey: queryKeys.community.posts(roomId ?? '', page),
        enabled: Boolean(roomId),
        queryFn: () => http.get(`/community/rooms/${roomId}/posts`, {
            query: { page },
            schema: pagedPlain(Post),
        }),
    });
}
/**
 * One post by id — `GET /community/rooms/{id}/posts/{postId}`.
 *
 * A thread screen used to find its post in page one of `useRoomPosts`, so a
 * link to anything older (a Mat Bot hit, a notification) opened as « The
 * discussion isn't loading ». Keyed under the list's prefix so the writes
 * that invalidate `['community','rooms',roomId,'posts']` refresh this too.
 */
export function useRoomPost(http, roomId, postId) {
    return useQuery({
        queryKey: queryKeys.community.post(roomId ?? '', postId ?? ''),
        enabled: Boolean(roomId && postId),
        queryFn: () => http.get(`/community/rooms/${roomId}/posts/${postId}`, { schema: Post }),
    });
}
export function useCreateRoomPost(http, roomId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ body, attachmentIds, poll }) => http.post(`/community/rooms/${roomId}/posts`, {
            body,
            ...(attachmentIds?.length ? { attachmentIds } : {}),
            ...(poll ? { poll } : {}),
        }, {
            schema: Post,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['community', 'rooms', roomId, 'posts'] }),
    });
}
export function useCreateComment(http, invalidate) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ postId, parentCommentId, body, attachmentIds, poll, }) => 
        // One level of nesting only — a reply to a reply is refused server-side,
        // so a reply to a reply is sent to the reply's own parent (9 Oct 2026).
        http.post(`/community/posts/${postId}/comments`, {
            body,
            ...(parentCommentId ? { parentCommentId } : {}),
            ...(attachmentIds?.length ? { attachmentIds } : {}),
            ...(poll ? { poll } : {}),
        }, {
            schema: Post,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: invalidate }),
    });
}
/**
 * Vote in a poll, or move the vote. One vote per member, so a repeat is the
 * same row — no key. Answers the post the poll sits on, results included.
 */
export function useVotePoll(http, invalidate) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ pollId, optionId }) => http.post(`/community/polls/${pollId}/vote`, { optionId }, { schema: Post }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: invalidate }),
    });
}
/** Close a poll — its author, or a moderator of the room. Results show to all. */
export function useClosePoll(http, invalidate) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (pollId) => http.post(`/community/polls/${pollId}/close`, {}, { schema: Post }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: invalidate }),
    });
}
/** Join or leave. Joining twice leaves one row and one moment — no key needed. */
export function useToggleRoomMembership(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ roomId, on }) => http.post(`/community/rooms/${roomId}/join`, { on }, { schema: Room }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.community.rooms }),
    });
}
/**
 * What the member has bookmarked.
 *
 * **The list re-applies the access guard server-side**, which is the whole
 * function rather than a precaution: a bookmark outlives the access that made
 * it, and without the check a member's own list becomes a permanent way back
 * into a room whose audience was narrowed afterwards. The bookmark row survives
 * — it is theirs — and the post stops being listed, so `total` counts what they
 * may still see rather than how many bookmarks they hold.
 */
export function useBookmarks(http, page = 1) {
    return useQuery({
        queryKey: ['community', 'bookmarks', page],
        queryFn: () => http.get('/community/bookmarks', { query: { page }, schema: pagedPlain(Post) }),
    });
}
/**
 * Bookmark, and un-bookmark — the same route with `{ on: false }`.
 */
export function useToggleBookmark(http, invalidate) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ postId, on }) => http.post(`/community/posts/${postId}/bookmark`, { on }, {
            schema: Post,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: invalidate }),
    });
}
/**
 * A room's named moderators — `.mod-list` in the design.
 *
 * ── Two routes, two schemas, and that is the point ────────────────────────
 * `GET /rooms/{id}/admins` is the BACK OFFICE read: `community.moderate`-gated
 * and carrying `email`, because managing an assignment needs an address.
 * `GET /rooms/{id}/moderators` is this one — no capability at all, any signed-in
 * member, and **no `email` on the wire**.
 *
 * The backend kept them as two schemas rather than one shape behind two gates
 * precisely so a staff address cannot arrive one day because somebody widened a
 * capability. We point at the member-facing one; the narrowing below stays as a
 * second line rather than the first.
 *
 * ── A room you cannot see is a 404, never a 403 ───────────────────────────
 * Same rule as Pro-only content: confirming a practitioner-only room exists at
 * an id is itself the leak. So there is no "you may not see this room's
 * moderators" state to render, and no retry policy to reason about — which is
 * why the `retry: false` this query used to carry is gone. It existed because
 * the admin route was a guaranteed 403 for a member.
 *
 * ── `title` exists after all ──────────────────────────────────────────────
 * The prototype gives each moderator a title ("Co-founder · M.Sc.") and a
 * presence dot. We recorded both as unbuildable. The title is real — it is the
 * profession off their practitioner profile, `null` when they have none — so it
 * renders. The presence dot is still nothing: it is a claim about whether
 * somebody is there to answer you, and no field behind it means no dot.
 */
export const RoomModerator = z.object({
    userId: z.string().uuid(),
    fullName: z.string(),
    title: z.string().nullish(),
});
const RoomModeratorList = z.object({ items: z.array(RoomModerator) });
export function useRoomModerators(http, roomId) {
    return useQuery({
        queryKey: ['community', 'rooms', roomId, 'moderators'],
        queryFn: () => http.get(`/community/rooms/${roomId}/moderators`, { schema: RoomModeratorList }),
        enabled: Boolean(roomId),
        /*
         * Inert by the contract's own description — "V1 ships no moderation path,
         * so holding this grants nothing". It says who is answerable, which is
         * exactly what the panel is for, and it changes about never.
         */
        staleTime: 5 * 60_000,
    });
}
/**
 * Who reacted to a post (9 Oct 2026) — one row per person, newest first, with
 * every kind they gave. Fetched only when the list is opened (`enabled`).
 * `photoUrl` is signed and short-lived; never persist it.
 */
export const PostReactor = z.object({
    user: z.object({ id: z.string(), fullName: z.string(), photoUrl: z.string().nullish() }),
    kinds: z.array(ReactionKind),
});
export const PostReactions = z.object({ items: z.array(PostReactor), total: z.number().int().nonnegative() });
export function usePostReactions(http, postId, enabled) {
    return useQuery({
        queryKey: ['community', 'post', postId, 'reactions'],
        queryFn: () => http.get(`/community/posts/${postId}/reactions`, { schema: PostReactions }),
        enabled,
    });
}
//# sourceMappingURL=community.js.map